import React from 'react';
import Grid from '@mui/material/GridLegacy';
import { Box, Skeleton, Card, CardContent } from '@mui/material';

/**
 * Full-page skeleton loader — replaces bare <CircularProgress /> spinners.
 *
 * Variants:
 *  - "table"   — header + n rows of skeleton cells (default)
 *  - "cards"   — grid of card skeletons (Dashboard / Reports)
 *  - "detail"  — a detail-page skeleton (single item with fields)
 *  - "form"    — a form skeleton (stacked fields)
 */
export default function PageLoader({ variant = 'table', rows = 6, cards = 6 }) {
  if (variant === 'cards') {
    return (
      <Box sx={{ p: 3 }}>
        {/* Page header skeleton */}
        <Skeleton variant="text" width={240} height={40} sx={{ mb: 1 }} />
        <Skeleton variant="text" width={160} height={24} sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          {Array.from({ length: cards }).map((_, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3 }}>
                <CardContent>
                  <Skeleton variant="rounded" height={14} width="40%" sx={{ mb: 1.5 }} />
                  <Skeleton variant="rounded" height={40} width="60%" sx={{ mb: 2 }} />
                  <Skeleton variant="rounded" height={10} width="80%" sx={{ mb: 1 }} />
                  <Skeleton variant="rounded" height={10} width="60%" />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (variant === 'detail') {
    return (
      <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
        {/* Breadcrumb + title */}
        <Skeleton variant="text" width={180} height={22} sx={{ mb: 1 }} />
        <Skeleton variant="text" width={300} height={44} sx={{ mb: 3 }} />

        {/* Status chip row */}
        <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
          {[100, 80, 120].map((w, i) => (
            <Skeleton key={i} variant="rounded" width={w} height={28} sx={{ borderRadius: 10 }} />
          ))}
        </Box>

        {/* Content card */}
        <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3, mb: 3 }}>
          <CardContent>
            <Grid container spacing={2}>
              {Array.from({ length: 8 }).map((_, i) => (
                <Grid item xs={12} sm={6} key={i}>
                  <Skeleton variant="text" width="40%" height={18} sx={{ mb: 0.5 }} />
                  <Skeleton variant="rounded" height={20} width="75%" />
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>

        {/* Action buttons */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Skeleton variant="rounded" width={120} height={40} sx={{ borderRadius: 2 }} />
          <Skeleton variant="rounded" width={120} height={40} sx={{ borderRadius: 2 }} />
        </Box>
      </Box>
    );
  }

  if (variant === 'form') {
    return (
      <Box sx={{ p: 3, maxWidth: 720, mx: 'auto' }}>
        <Skeleton variant="text" width={220} height={40} sx={{ mb: 3 }} />
        {Array.from({ length: rows }).map((_, i) => (
          <Box key={i} sx={{ mb: 2.5 }}>
            <Skeleton variant="text" width="30%" height={20} sx={{ mb: 0.75 }} />
            <Skeleton variant="rounded" height={48} sx={{ borderRadius: 1.5 }} />
          </Box>
        ))}
        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          <Skeleton variant="rounded" width={140} height={42} sx={{ borderRadius: 2 }} />
          <Skeleton variant="rounded" width={100} height={42} sx={{ borderRadius: 2 }} />
        </Box>
      </Box>
    );
  }

  // Default: "table" variant
  return (
    <Box sx={{ p: 3 }}>
      {/* Page header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Skeleton variant="text" width={200} height={38} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width={140} height={22} />
        </Box>
        <Skeleton variant="rounded" width={130} height={40} sx={{ borderRadius: 2 }} />
      </Box>

      {/* Filter / search bar */}
      <Skeleton variant="rounded" height={52} sx={{ borderRadius: 2, mb: 2.5 }} />

      {/* Table header */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${Math.min(rows, 5)}, 1fr)`,
          gap: 2,
          px: 2,
          py: 1.5,
          bgcolor: '#f1f5f9',
          borderRadius: '8px 8px 0 0',
          border: '1px solid #e2e8f0',
          borderBottom: 'none',
        }}
      >
        {Array.from({ length: Math.min(rows, 5) }).map((_, i) => (
          <Skeleton key={i} variant="text" width="70%" height={18} />
        ))}
      </Box>

      {/* Table rows */}
      {Array.from({ length: 7 }).map((_, rowIdx) => (
        <Box
          key={rowIdx}
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${Math.min(rows, 5)}, 1fr)`,
            gap: 2,
            px: 2,
            py: 1.75,
            border: '1px solid #e2e8f0',
            borderTop: 'none',
            bgcolor: rowIdx % 2 === 0 ? '#fff' : '#fafbfc',
            '&:last-child': { borderRadius: '0 0 8px 8px' },
          }}
        >
          {Array.from({ length: Math.min(rows, 5) }).map((_, colIdx) => (
            <Skeleton
              key={colIdx}
              variant="rounded"
              height={16}
              width={colIdx === 0 ? '85%' : colIdx % 2 === 0 ? '60%' : '75%'}
              sx={{ borderRadius: 1 }}
            />
          ))}
        </Box>
      ))}
    </Box>
  );
}
